/**
 * i18next backend that loads EVERY namespace, for every locale, on demand via
 * Vite's `import.meta.glob`. One chunk per locale × namespace.
 *
 * It used to cover only the "heavy" namespaces, with `common`, `layout`, `auth2`
 * and `booking` statically imported in index.ts for all six locales. That static
 * half was the 103 KB gzipped `i18n-*.js` chunk, modulepreloaded at top priority,
 * of which any one session could use exactly one sixth (IN-46 Phase 3). Nothing is
 * bundled now: the detector knows the language synchronously, so the fetch for the
 * detected locale's core namespaces starts at module evaluation and an English user
 * pays for English only.
 *
 * The glob must stay a STATIC, literal pattern. A parameterised dynamic import
 * makes Vite give up and pull every locale into the main chunk — the shape this
 * file originally existed to avoid (INEFFECTIVE_DYNAMIC_IMPORT). `*` wildcards are
 * fine; a template variable is not.
 */
import type { BackendModule, ResourceLanguage } from 'i18next'

const lazyModules = import.meta.glob<{ default: ResourceLanguage }>('./locales/*/*.json')

const LazyBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(
    language: string,
    namespace: string,
    callback: (err: Error | null, data?: ResourceLanguage | false) => void,
  ) {
    const key = `./locales/${language}/${namespace}.json`
    const loader = lazyModules[key]
    if (!loader) {
      // No such locale/namespace pair on disk. With fallbackLng: false there is
      // no English safety net, so this must not happen in shipped code — it is
      // exactly what locale-coverage.test.ts fails the build over. Return empty
      // rather than throwing: a raw key is bad, a blank screen is worse.
      callback(null, {})
      return
    }
    loader()
      .then(mod => callback(null, mod.default))
      .catch(() => callback(null, {}))
  },
}

export default LazyBackend
